/**
 * @file utils/weeklyReview.js
 * @description Weekly calorie-deficit aggregation, insights, tips and highlights.
 *
 * Groups meals into Monday-based weeks and compares the deficit the user
 * actually achieved against their weekly goal (derived from their targets).
 */

import { calculateUserTargets } from "./calculateUserTargets";

// ~7700 kcal ≈ 1 kg of body fat.
const KCAL_PER_KG = 7700;

/** Handle both JS Date and Firebase Timestamps. */
export const parseAnyDate = (date) => {
  if (!date) return new Date();
  return date.seconds ? new Date(date.seconds * 1000) : new Date(date);
};

/** Local yyyy-mm-dd key (avoids UTC off-by-one). */
export function dayKey(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

/** Monday 00:00 of the week containing `date`. */
export function getWeekStart(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const dow = (d.getDay() + 6) % 7; // 0 = Monday
  d.setDate(d.getDate() - dow);
  return d;
}

function formatRange(start, end) {
  const sameMonth = start.getMonth() === end.getMonth();
  const opts = { month: "short", day: "numeric" };
  const s = start.toLocaleDateString("en-US", opts);
  const e = end.toLocaleDateString(
    "en-US",
    sameMonth ? { day: "numeric" } : opts,
  );
  return `${s} – ${e}`;
}

/**
 * Builds weekly deficit aggregates from meal logs.
 * @param {Array} meals
 * @param {object} profileData
 * @returns {{ maintenance: number|null, dailyGoalDeficit: number, proteinTarget: number|null, weeks: Array }}
 */
export function buildWeeklyDeficits(meals = [], profileData = {}) {
  const targets = calculateUserTargets(profileData);
  const maintenance =
    typeof targets.maintenance === "number" ? Math.round(targets.maintenance) : null;
  const dailyGoalDeficit =
    typeof targets.adjustmentValue === "number"
      ? Math.max(0, -targets.adjustmentValue)
      : 0;
  const proteinTarget =
    typeof targets.protein === "number" ? targets.protein : null;

  if (!maintenance || !meals?.length) {
    return { maintenance, dailyGoalDeficit, proteinTarget, weeks: [] };
  }

  // 1. Aggregate per day.
  const days = {};
  meals.forEach((m) => {
    const key = dayKey(parseAnyDate(m.createdAt));
    if (!days[key]) days[key] = { calories: 0, protein: 0 };
    days[key].calories += m.calories || 0;
    days[key].protein += m.protein || 0;
  });

  // 2. Group days into weeks.
  const weekMap = {};
  Object.entries(days).forEach(([key, totals]) => {
    if (totals.calories <= 0) return;
    const start = getWeekStart(key);
    const wk = dayKey(start);
    if (!weekMap[wk]) weekMap[wk] = { start, dayList: [] };
    weekMap[wk].dayList.push({ date: key, ...totals });
  });

  // 3. Compute aggregates per week.
  const weeks = Object.values(weekMap)
    .map(({ start, dayList }) => {
      const end = new Date(start);
      end.setDate(end.getDate() + 6);

      const daysLogged = dayList.length;
      const totalEaten = dayList.reduce((s, d) => s + d.calories, 0);
      const totalProtein = dayList.reduce((s, d) => s + d.protein, 0);
      const avgEaten = Math.round(totalEaten / daysLogged);
      const avgProtein = Math.round(totalProtein / daysLogged);

      // Deficit accumulated only on days that were actually logged.
      const actualDeficit = maintenance * daysLogged - totalEaten;
      const goalDeficit = dailyGoalDeficit * 7;
      const avgDailyDeficit = Math.round(actualDeficit / daysLogged);
      const estKgChange = actualDeficit / KCAL_PER_KG;

      const sorted = [...dayList].sort((a, b) => a.calories - b.calories);

      return {
        weekStart: start,
        weekEnd: end,
        key: dayKey(start),
        label: formatRange(start, end),
        daysLogged,
        totalEaten,
        avgEaten,
        avgProtein,
        actualDeficit: Math.round(actualDeficit),
        goalDeficit: Math.round(goalDeficit),
        avgDailyDeficit,
        estKgChange,
        bestDay: sorted[0] || null, // lowest calories = biggest deficit
        highestDay: sorted[sorted.length - 1] || null,
      };
    })
    .sort((a, b) => a.weekStart - b.weekStart);

  return { maintenance, dailyGoalDeficit, proteinTarget, weeks };
}

/**
 * Generates a headline, tips and highlights for a single week.
 * @param {object} week - a week object from buildWeeklyDeficits
 * @param {object} ctx - { dailyGoalDeficit, proteinTarget }
 */
export function getWeeklyInsights(week, ctx = {}) {
  const { dailyGoalDeficit = 0, proteinTarget = null } = ctx;
  const tips = [];
  const highlights = [];

  const onTrack =
    dailyGoalDeficit > 0 &&
    week.avgDailyDeficit >= dailyGoalDeficit * 0.9;
  const surplus = week.avgDailyDeficit < 0;

  // Headline + emoji based on performance.
  let headline;
  let emoji;
  if (surplus) {
    headline = "You ate above maintenance this week";
    emoji = "📈";
  } else if (onTrack) {
    headline = "You crushed your weekly deficit!";
    emoji = "🔥";
  } else if (week.avgDailyDeficit > 0) {
    headline = "You stayed in a deficit this week";
    emoji = "✅";
  } else {
    headline = "You held maintenance this week";
    emoji = "⚖️";
  }

  // ── Highlights ──────────────────────────────────────────────
  highlights.push(
    `Averaged ${week.avgEaten.toLocaleString()} kcal/day across ${week.daysLogged} logged ${
      week.daysLogged === 1 ? "day" : "days"
    }.`,
  );

  if (week.estKgChange > 0.05) {
    highlights.push(
      `On pace to lose ~${week.estKgChange.toFixed(2)} kg of fat.`,
    );
  } else if (week.estKgChange < -0.05) {
    highlights.push(
      `Trended toward gaining ~${Math.abs(week.estKgChange).toFixed(2)} kg.`,
    );
  }

  if (week.bestDay) {
    const d = new Date(week.bestDay.date).toLocaleDateString("en-US", {
      weekday: "long",
    });
    highlights.push(
      `Best day was ${d} — just ${week.bestDay.calories.toLocaleString()} kcal.`,
    );
  }

  if (proteinTarget) {
    const pct = Math.round((week.avgProtein / proteinTarget) * 100);
    highlights.push(
      `Hit ${pct}% of your protein goal on average (${week.avgProtein}g/day).`,
    );
  }

  // ── Tips ────────────────────────────────────────────────────
  if (week.daysLogged < 5) {
    tips.push(
      "Log every day next week — consistent tracking makes your deficit numbers far more accurate.",
    );
  }

  if (surplus) {
    tips.push(
      "You were above maintenance. Trim ~300–500 kcal/day by swapping high-calorie snacks for protein and veg.",
    );
  } else if (dailyGoalDeficit > 0 && week.avgDailyDeficit < dailyGoalDeficit * 0.9) {
    const gap = Math.round(dailyGoalDeficit - week.avgDailyDeficit);
    tips.push(
      `You were about ${gap} kcal/day short of your goal deficit. A short daily walk or smaller portions can close that gap.`,
    );
  } else if (onTrack) {
    tips.push(
      "You're right on target — keep the same routine and your weekly weigh-ins should reflect it soon.",
    );
  }

  if (proteinTarget && week.avgProtein < proteinTarget * 0.8) {
    tips.push(
      "Protein was a little low. Aim to include a protein source in every meal to protect muscle in a deficit.",
    );
  }

  if (!tips.length) {
    tips.push("Great balance this week. Stay the course!");
  }

  return { headline, emoji, tips, highlights, onTrack, surplus };
}
